import {
  Box,
  Button,
  Flex,
  Heading,
  Switch,
  Text,
  useToast,
} from '@chakra-ui/react';
import {
  deleteTermSheetApi,
  listTermSheetsApi,
  updateTermSheetSentApi,
} from 'api/termSheetApi';
import DataTable from 'components/DataTable';
import TwMenu from 'components/menu/Menu';
import Card from 'components/card/Card';
import { createColumnHelper } from '@tanstack/react-table';
import { CockpitUsersContext } from 'context/CockpitUsersContext';
import moment from 'moment';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CockpitCustomerContext } from 'context/CockpitCustomerContext';

const columnHelper = createColumnHelper();

export default function TermSheetList() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { customer } = useContext(CockpitCustomerContext);
  const { getUserName } = useContext(CockpitUsersContext);
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const loadRows = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await listTermSheetsApi({ customerId });
      setRows(data ?? []);
    } catch (error) {
      toast({
        title: 'Failed to load term sheets',
        description: error?.message || String(error),
        status: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  }, [customerId, toast]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const onToggleSent = async (row, sentToClient) => {
    try {
      await updateTermSheetSentApi({
        customerId,
        termSheetId: row.id,
        sentToClient,
      });
      await loadRows();
    } catch (error) {
      toast({
        title: 'Failed to update sent status',
        description: error?.message || String(error),
        status: 'error',
      });
    }
  };

  const onDelete = async (id) => {
    try {
      await deleteTermSheetApi({ customerId, termSheetId: id });
      await loadRows();
    } catch (error) {
      toast({
        title: 'Delete failed',
        description: error?.response?.data?.error || error?.message || String(error),
        status: 'error',
      });
    }
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor('sequenceNumber', {
        header: '#',
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('name', {
        header: 'NAME',
        cell: (info) => info.getValue() || '—',
      }),
      columnHelper.accessor('createdAt', {
        header: 'CREATED',
        cell: (info) =>
          info.getValue()
            ? moment(info.getValue()).format('MMM D, YYYY HH:mm')
            : '—',
      }),
      columnHelper.accessor('createdBy', {
        header: 'CREATED BY',
        cell: (info) => getUserName(info.getValue()) || info.getValue() || '—',
      }),
      columnHelper.accessor('sentToClient', {
        header: 'SENT TO CLIENT',
        cell: (info) => (
          <Switch
            isChecked={Boolean(info.getValue())}
            onChange={(event) => {
              event.stopPropagation();
              onToggleSent(info.row.original, event.target.checked);
            }}
          />
        ),
      }),
      columnHelper.display({
        id: 'actions',
        cell: (info) => {
          const row = info.row.original;
          if (row.sentToClient) {
            return null;
          }
          return (
            <TwMenu
              items={[
                {
                  label: 'Delete',
                  onClick: (event) => {
                    event.stopPropagation();
                    onDelete(row.id);
                  },
                },
              ]}
            />
          );
        },
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [customerId, getUserName]
  );

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Heading size="lg">Term Sheet</Heading>
          <Text color="gray.600" mt={1}>
            {customer?.name || customerId}
          </Text>
        </Box>
        <Button
          colorScheme="brand"
          onClick={() => navigate(`/customers/${customerId}/term-sheets/new`)}
        >
          Create new term sheet
        </Button>
      </Flex>
      <Card>
        <DataTable
          columns={columns}
          data={rows}
          isLoading={isLoading}
          onRowClick={(row) =>
            navigate(`/customers/${customerId}/term-sheets/${row.id}`)
          }
        />
      </Card>
    </Box>
  );
}
